import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { WHACKY_WHALES_CONTRACT } from '@gilding/shared';
import { api } from '../api/client.js';
import { GoldSplash } from '../components/GoldSplash.js';
import { GildButton } from '../components/GildButton.js';

/**
 * EarlyAccess — the gate a signed-in NON-holder lands on.
 *
 * The public teaser (splash → corridor → one free gild) is open to everyone, but
 * the full site is holder-gated until public launch. This page — same dark-luxury
 * world as the splash cover — tells them so, confirms their free gild is saved,
 * and lets them RE-VERIFY on-chain (for someone who just bought a whale) or switch
 * wallets. If the re-check finds a whale, they're granted their signup credits and
 * dropped straight into the app.
 */
export function EarlyAccess() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const meQuery = useQuery({ queryKey: ['me'], queryFn: () => api.me(), staleTime: 30_000 });
  const [checking, setChecking] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const regildRef = useRef<(() => void) | null>(null);
  const onSettled = useCallback(() => setRevealed(true), []);
  const bindRegild = useCallback((fn: () => void) => {
    regildRef.current = fn;
  }, []);

  // Already a holder (or promoted since arriving)? Don't strand them here.
  useEffect(() => {
    if (meQuery.data?.authenticated && meQuery.data.holder === true) {
      navigate('/dashboard', { replace: true });
    }
    // Not signed in at all → back to the corridor to connect.
    if (meQuery.isSuccess && meQuery.data?.authenticated === false) {
      navigate('/', { replace: true });
    }
  }, [meQuery.data, meQuery.isSuccess, navigate]);

  // Safety net so the CTA never stays hidden if the settle callback is missed.
  useEffect(() => {
    const t = window.setTimeout(() => setRevealed(true), 6000);
    return () => window.clearTimeout(t);
  }, []);

  const onReverify = async (): Promise<void> => {
    if (checking) return;
    setChecking(true);
    setNotFound(false);
    try {
      const res = await api.reverify();
      await queryClient.invalidateQueries({ queryKey: ['me'] });
      if (res.holder) {
        navigate('/dashboard', { replace: true });
      } else {
        setNotFound(true);
      }
    } catch {
      setNotFound(true);
    } finally {
      setChecking(false);
    }
  };

  const onSwitchWallet = async (): Promise<void> => {
    try {
      await api.logout();
    } catch {
      /* ignore */
    }
    await queryClient.invalidateQueries({ queryKey: ['me'] });
    navigate('/', { replace: true });
  };

  return createPortal(
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{
        background:
          'radial-gradient(120% 95% at 50% 30%, #0f0d12 0%, #08070a 50%, #030304 100%)',
      }}
    >
      <h1 className="sr-only">Gilding early access</h1>
      <GoldSplash className="absolute inset-0" onSettled={onSettled} bindRegild={bindRegild} />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 z-10"
        style={{
          background: 'radial-gradient(125% 95% at 50% 38%, transparent 38%, rgba(0,0,0,.82) 100%)',
        }}
      />

      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex flex-col items-center gap-4 px-6 pb-12 text-center transition-all duration-1000"
        style={{
          opacity: revealed ? 1 : 0,
          transform: revealed ? 'translateY(0)' : 'translateY(14px)',
        }}
      >
        <p
          className="font-body uppercase"
          style={{ color: '#c9a546', fontSize: '0.62rem', letterSpacing: '0.34em' }}
        >
          Early access
        </p>
        <p
          className="font-display italic"
          style={{ color: '#cdb27a', fontSize: 'clamp(1.05rem,2.4vw,1.5rem)' }}
        >
          Gilding opens to everyone soon.
        </p>
        <p
          className="max-w-md font-body"
          style={{ color: '#8a774f', fontSize: '0.84rem', lineHeight: 1.7 }}
        >
          Holding a{' '}
          <span style={{ color: '#c9a546' }}>Whacky Whale</span> unlocks the full site. Verify your
          wallet to step inside. Your free gild is safe, saved to this wallet and waiting for you.
        </p>

        <div className="mt-1 flex items-center gap-3" aria-hidden="true">
          <span className="h-px w-10" style={{ background: 'linear-gradient(90deg,transparent,#c9a546aa)' }} />
          <span
            className="inline-block h-1.5 w-1.5 rotate-45"
            style={{ background: 'linear-gradient(135deg,#fff3ce,#a87f2c)' }}
          />
          <span className="h-px w-10" style={{ background: 'linear-gradient(90deg,#c9a546aa,transparent)' }} />
        </div>

        <GildButton
          className="pointer-events-auto mt-2"
          onClick={() => void onReverify()}
          disabled={checking}
        >
          {checking ? 'Checking the chain…' : 'Re-verify wallet'}
        </GildButton>

        {notFound && (
          <p
            className="max-w-sm font-body"
            style={{ color: '#b9855a', fontSize: '0.76rem', lineHeight: 1.6 }}
          >
            No qualifying holding was verified. Check that you connected the right wallet and network, then try again.
          </p>
        )}

        <div className="pointer-events-auto mt-1 flex flex-col items-center gap-2">
          <a
            href={`https://opensea.io/contract/ethereum/${WHACKY_WHALES_CONTRACT}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-body uppercase transition-colors"
            style={{ color: '#9d8757', fontSize: '0.62rem', letterSpacing: '0.22em' }}
          >
            Find a Whacky Whale →
          </a>
          <button
            type="button"
            onClick={() => void onSwitchWallet()}
            className="font-body uppercase transition-colors hover:text-[#c9a546]"
            style={{ color: '#6a5c3d', fontSize: '0.58rem', letterSpacing: '0.2em' }}
          >
            Use a different wallet
          </button>
        </div>

        <p
          className="max-w-sm font-body leading-relaxed"
          style={{ color: '#5f5236', fontSize: '0.58rem' }}
        >
          Not affiliated with or endorsed by Whacky Whales. Access is a product feature, not
          financial advice. The operator of Gilding holds some of the collection.
        </p>
      </div>
    </div>,
    document.body,
  );
}
